import axios from 'axios'
import _ from "lodash";
import { Message } from '@arco-design/web-vue';
import { ElLoading } from "element-plus"
import config from "@/config/baseUrl.js"
import { tokenStore } from "@/store/modules/tokenUse.js"
import { userStore } from "@/store/modules/user";
import { authLoginStore } from "@/store/modules/autoLogin";
import { menuStore } from "@/store/modules/menuUse.js";
import router from '@/router'
import useI18n from "@/i18n/index.js";
const { t } = useI18n.global;

axios.defaults.baseURL = config.baseURL + '/api';
axios.defaults.timeout = 60 * 1000; // 超时时间/s

// request interceptor
axios.interceptors.request.use(config => {
    const { getToken } = tokenStore();
    const token = getToken()
    // 如果token存在，每个接口加上token
    if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
    }
    // 递归给空字段赋值为undefined，浏览器自动过滤掉undefined字段，后端不会报错
    if (config.method === 'post' && config.data) {
        let data = _.cloneDeep(config.data);
        function recursion(data) {
            Object.keys(data).map(key => {
                if (data[key] === null || data[key] === "" || typeof data[key] === 'number' && isNaN(data[key])) {
                    data[key] = undefined;
                } else if (typeof data[key] === 'object') {
                    recursion(data[key])
                }
            })
        }
        recursion(data);
        config.data = data;
    }
    return config
}, (error) => {
    return Promise.reject(error)
})

// response interceptor
axios.interceptors.response.use(async (response) => {
    const { setToken } = tokenStore();
    // 获取返回的token
    const token = response.headers?.["access-token"];
    const refuseToken = response.headers?.["x-access-token"];
    if (token && refuseToken) setToken({ token, refuseToken });
    if (response.data.statusCode !== 200 || !response.data.succeeded) {
        if (typeof response.data.errors === 'string') {
            Message.error(response.data.errors)
        } else {
            let temp = Object.keys(response.data?.errors) ?? []
            let arr = [];
            temp.forEach(item => {
                arr.push(...response.data.errors[item])
            })
            arr.forEach((item) => {
                Message.error(item)
            })
        }
        console.error(new Error(`${response.request.responseURL} 接口报错`));
        return Promise.reject(response.data)
    }
    return response.data
})

// 全部清除
async function clearAll() {
    const { removeToken } = tokenStore();
    const { setMenuList, setTabList } = menuStore();
    const { removeUserInfo } = userStore();
    const { clearAllAutoLogin } = authLoginStore();
    await Promise.all([
        setMenuList(),
        setTabList(),
        removeToken(),
        removeUserInfo(),
        clearAllAutoLogin(),
    ]);
    router.push("/login");
}

/**
 * 判断接口是否401，是否重新请求
 */
function is401({ resolve = () => { }, reject = () => { } }, err, arg) {
    const { getRefuseToken } = tokenStore();
    const refuseToken = getRefuseToken();
    if (err?.response?.status !== 401 || !refuseToken) return reject(err);
    // token过期后用刷新token重新请求
    arg[0].headers = { "x-Authorization": `Bearer ${refuseToken}` }
    axios(...arg).then(resolve).catch(async (refuseError) => {
        if (refuseError?.response?.status !== 401) return reject(refuseError);
        // 如果报错还是401，说明刷新token也过期了，返回到登录页
        Message.warning(t('otherMsg.loginExpired'));
        await clearAll();
        reject()
    })
}

let interval = null;
const originalTime = 30;
let time = originalTime;
const whitelistRoute = ["/login"];
async function startConnect({ resolve = () => { }, reject = () => { } }, arg) {
    // 白名单页面直接提示，不尝试请求
    if (whitelistRoute.includes(location.pathname)) {
        Message.error({
            id: "axios error",
            content: t("otherMsg.serviceError")
        })
        clearAll();
        return reject()
    }
    if (interval) return reject();
    // 开启全局loading
    const loadingInstance = ElLoading.service({
        target: "#app",
        text: t('otherMsg.serviceLoading'),
        customClass: "serviceLoading"
    })
    // 控制while循环是否停止
    let isStop = false;
    let setArr = [];
    // 创建一个队列，如果塞入有true的数据，不接受后续的数据传递
    function setStop(val) {
        if (setArr.includes(true)) {
            return (isStop = true)
        }
        setArr.push(val);
    }
    // 停止并重置
    function stop() {
        setStop(true)
        interval && clearInterval(interval)
        interval = null;
        time = originalTime;
        loadingInstance.close();
    }
    interval = setInterval(() => {
        time--;
        if (time <= 0) {
            stop()
            // 返回到登陆
            Message.error({
                id: "axios error",
                content: t("otherMsg.serviceError")
            })
            clearAll();
            reject();
        }
    }, 1000);
    while (!isStop) {
        try {
            const res = await axios(...arg)
            stop()
            resolve(res);
        } catch (error) {
            // 还是连接失败，等待1秒后再查询
            if (error.code === "ERR_NETWORK") {
                await new Promise(resolve => {
                    const tOut = setTimeout(() => {
                        resolve()
                        clearTimeout(tOut)
                    }, 1000)
                });
                setStop(false);
            } else {
                stop()
                is401({ resolve, reject }, err, arg);
            }
        }
    }
}

// 401重新请求
function newService() {
    return new Promise((resolve, reject) => {
        axios(...arguments).then(resolve).catch(async (err) => {
            // 接口连接错误失败
            if (err.code === "ERR_NETWORK") {
                // 客户端接口，调用失败
                if (err.config.url.includes(config.clientUrl)) {
                    return reject();
                }
                startConnect({ resolve, reject }, arguments);
            } else {
                is401({ resolve, reject }, err, arguments)
            }
        })
    })
}

export default newService;